<template>
	<div
		v-if="ratingOverallValue"
		:class="['customer-reviews__rating', blockClassModifiers]"
	>
		<component
			:is="isGalleryVariant ? 'a' : 'div'"
			v-bind="dynamicAttributes"
			class="customer-reviews__content"
			@click.prevent="isGalleryVariant && scrollToAnchorWithDynamicOffset($event)"
		>
			<CustomerReviewsRatingContent
				:rating-label="ratingLabel"
				class="customer-reviews__rating-content"
				:recommendation-rate
				:rating-count="ratingCount"
			/>
			<RatingBox
				class="customer-reviews__rating-badge"
				:rating="ratingFixedPerSix"
			/>
		</component>
	</div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import RatingBox from '@lmt-rpb/ReviewBox/internals/RatingBox.vue';
import { scrollToAnchorWithDynamicOffset as scrollToAnchorWithDynamicOffsetOriginal, scrollIt } from '@utils/utils';
import CustomerReviewsRatingContent from './internals/CustomerReviewsRatingContent.vue';
import { gaSelectContentAttr } from 'src/utils/analytics';
import { numberFormat } from '@/js/utils/numberUtils';
import { ratingOverall } from '@utils/utils';
import { useSearchParams } from '../../composables/searchParams';

interface Props {
	ratingLabel: string;
	recommendationRate: number;
	ratingCount: number;
	ratingOverallValue: number;
	rating?: number;
	theme?: 'beforeGallery' | 'afterGallery';
	beforeGallery?: boolean;
	afterGallery?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	beforeGallery: false,
	afterGallery: false,
	prefix: '',
});

const isGalleryVariant = computed(() => props.beforeGallery || props.afterGallery);
const dynamicAttributes = computed(() => ({
	...(isGalleryVariant.value ? { href: navLink.value, ...gaSelectContentAttr({ cId: 'hotelinfo_review' }) } : {}),
}));

const urlPathname = ref('');
const urlSearchParams = useSearchParams();
const navLink = computed(() => `${urlPathname.value}${urlSearchParams.value}#reviews`);
const animationInProgress = ref(false);
const animationEndCallback = () => {
	animationInProgress.value = false;
};

const scrollToAnchorWithDynamicOffset = (event: MouseEvent): void => {
	animationInProgress.value = true;
	scrollToAnchorWithDynamicOffsetOriginal(event, animationEndCallback);
};
const blockClassModifiers = computed(() => ({
	'customer-reviews__rating--before-gallery': props.beforeGallery,
	'customer-reviews__rating--after-gallery': props.afterGallery,
}));

const recommendationRate = `${numberFormat(props.recommendationRate, 0)}%`;
const ratingFixedPerSix = ratingOverall(props.ratingOverallValue);
onMounted((): void => {
	urlPathname.value = window.location.pathname;
	const urlHash = window.location.href.split('#')[1];
	const targetElement = document.querySelector(`#${urlHash}`) as HTMLElement;
	if (targetElement) {
		setTimeout(() => {
			scrollIt(targetElement, 0, 'linear', 50);
		}, 10);
	}
});
</script>

<style lang="scss" scoped>
.customer-reviews {
	&__content {
		display: flex;
		flex-direction: row-reverse;

		& > * {
			pointer-events: none;
		}
	}

	&__recommendation {
		font-size: $font-small-2;
		width: 100%;

		@media (min-width: $breakpoint-small) {
			&,
			&-reviews {
				font-size: $font-small-1;
			}
		}

		.customer-reviews__rating--before-gallery &,
		.customer-reviews__rating--after-gallery {
			font-size: $font-small-2;
			line-height: 1.6;
			max-width: min-content;
			white-space: nowrap;

			@media (min-width: $breakpoint-small) {
				font-size: $font-small-1;
				line-height: $line-height-default;
			}

			@media (min-width: $breakpoint-extralarge) {
				font-size: $font-medium-2;
			}
		}
	}

	&__rating {
		display: flex;
		font-family: $font-family-special;
		margin-bottom: 1.6rem;
		width: 100%;
		align-items: center;

		&-text-percentage {
			font-variant-numeric: lining-nums;
		}

		@include media-query-up($breakpoint-small) {
			margin-bottom: 2.4rem;
		}

		&-badge {
			margin: 0 2.4rem 0 0;

			.customer-reviews__rating--before-gallery &,
			.customer-reviews__rating--after-gallery & {
				font-size: $font-medium-3;
				line-height: $line-height-small;
				padding: 0.8rem;
				height: 4.2rem;
				min-width: 4.2rem;

				@media (min-width: $breakpoint-extralarge) {
					font-size: $font-large-4;
					line-height: $line-height-default;
					height: 5.9rem;
					min-width: 5.9rem;
				}
			}
		}

		&--after-gallery {
			> a {
				padding: 0 $padding-normal;
				gap: 0 2.4rem;
				flex-direction: row-reverse;
				margin: 0.8rem 0 1.6rem;
				justify-content: flex-end;
			}

			.customer-reviews__rating-text-container {
				align-items: flex-start;
			}
		}

		&--before-gallery {
			justify-content: flex-end;

			.customer-reviews__content {
				gap: 0 1.6rem;
				align-items: center;
				flex-direction: row;
			}

			.customer-reviews__rating-text-container {
				@media (min-width: $breakpoint-small) {
					align-items: flex-start;
				}

				@media (min-width: $breakpoint-extralarge) {
					align-items: flex-end;
				}
			}
		}
	}

}
</style>
